import './LoggedBlock.css';

import React from 'react';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import HomeTab from './tabs/HomeTab';
import UserPopover from './users/UserPopover';
import SettingsPopover from './settings/SettingsPopover';
import AutoCompleteGlobalSearch from './common/AutoCompleteGlobalSearch';
import Action from './common/Action';
import _ from 'lodash';
import HelpIcon from './common/help/HelpIcon';
import {
  ALL_FILES_OPTION,
  ASSET_FILTERS,
  ASSETS_TAB_ARGS,
  BREADCRUMB_ASSETS,
  CATEGORY_LIST,
  COLLECTIONS,
  GLOBAL_PROGRESS,
  GLOBAL_SEARCH_INFO,
  IS_ASSET_SIDE_TREE_EXPANDED,
  IS_DRAGGING_FILE,
  NOTIFICATION_INFO,
  SHOW_FOLDERNAV_VIEW,
  SHOW_TREE_VIEW,
  SHOW_UPLOADS,
  SUCCESS_DIALOG_MSG
} from './store/Constants';

import {
  ApplyFilterMsg,
  ApplyFilterQuery,
  ApplyFilterResp,
  AssetIdJson,
  CategoryChangeMsg,
  CategoryChangeQuery,
  CurrentContextInfo,
  ExtBrowsingCapabilities,
  FilesResponseData,
  GetCommentsUnreadCountQuery,
  GetFacetsMsg,
  GetFacetsQuery,
  GetFacetsRes,
  LoadNextSearchPageMsg,
  LoadNextSearchPageQuery,
  ReloadSessionMsg,
  ReloadSessionQuery,
  RepoItem,
  SearchMessage,
  SearchQuery,
  ZmCollection
} from './autogenerated/ZoomBridge';

import Authenticator from './protocol/Authenticator';
import StringUtil from './util/StringUtil';
import ArrayUtil from './util/ArrayUtil';
import HashMap from './protocol/HashMap';
import SearchParamUtil from './util/SearchParamUtil';
import UploadingsItem from './dnd/UploadingsItem';
import UploadingsModal from './dnd/UploadingsModal';
import DropzoneModal from './dnd/DropzoneModal';
import ErrorHandler from './error/ErrorHandler';
import ThumbnailUtil from './util/ThumbnailUtil';
import NotificationPanel from './components/notification/NotificationPanel';
import NotificationController from './controllers/NotificationController';
import MetadataSpec from './queries/metadata/MetadataSpec';
import ViewUtil, { getParamsFromUrl } from './ViewUtil';
import AnnotationUtil from './queries/annotations/AnnotationUtil';
import CollectionUtil from './queries/lightbox/CollectionUtil';
import { togglePageLoadPlaceholder } from './util/DOMUtil';
import ParamsUtil from './util/ParamsUtil';
import A from './common/elements/A';
import { Visibility, KeyCode, TABS, SUBTAB } from './util/Constants';
import Button from './common/Button';
import { getScreenClassName, isMobile } from './util/ScreenUtil';
import Store from './store/Store';
import UnreadCommentsData from './queries/annotations/UnreadCommentsData';
import Toast from './common/Toast';
import {
  canShowAssetsTab,
  canShowBrowseExtTab,
  canShowCollectionsTab,
  canShowHomeTab,
  canShowSearchTab,
  showReportingTab
} from './util/FeatureUtil';
import EditUserAccount from './common/EditUserAccount';
import PublicBreadcrumb from './public/PublicBreadcrumb';
import StoreListener from './store/StoreListener';
import BackgroundJobRunner from './util/runner/BackgroundJobRunner';
import MetadataUtil from './util/MetadataUtil';
import MaintainSessionWithPreviewServer from './auth/MaintainSessionWithPreviewServer';
import t from './i18n/I18n';
import System from './basic/System';
import { createNotification } from './basic/Notification';
import { ThemeSetup } from './themes/ThemeSetup';
import { LoaderSpinner } from './common/Spinner';
import SuccessToast, { SuccessDailog, TPM_DIALOG_MSG } from './common/SuccessDialog';
import Lazyload from './lazyload/LazyLoad';
import NotificationIcon from './common/NotificationIcon';
import { getAssetPathFuids } from './util/RepoItemUtil';
import KeydownConfig from './keyboard-shortcuts/KeydownConfig';
import KeyboardShortcuts from './keyboard-shortcuts/KeyboardShortcuts';
import { clearAssetFilters } from './store/StoreUtil';
import SettingsTab from './tabs/settings/SettingsTab';
import ConfirmModal from './modals/confirm/ConfirmModal';
import GoBackAndForthPages from './GoBackAndForthPages';
import BrowseExt from './tabs/browse-external/BrowseExt';
import GlobalShortcuts from './hoc/GlobalShortcuts';
import HelpUtil from './util/HelpUtil';
import HelpWrapper from './common/help/HelpWrapper';
import Portal from './common/Portal';
import Popover from './Popover';
import { BrowseExtUtil } from './tabs/browse-external/BrowseExtUtil';
import Storage from './protocol/Storage';
import { SetterStoreUtil } from './store/setterStoreUtil';
const AssetsTab = Lazyload(() => import('./tabs/AssetsTab'));
const CollectionsTab = Lazyload(() => import('./tabs/collections/CollectionsTab'));
const ReportingTab = Lazyload(() => import('./reporting/ReportingTab'));
const MobileHeader = Lazyload(() => import('./mobile/MobileHeader'));
const AdvancedSearch = Lazyload(() => import('./advanced-search/AdvancedSearch'));

const debug = require('./debug/debug')('wc:LoggedBlock');

const storeItems = [IS_DRAGGING_FILE, UnreadCommentsData.NAME, SHOW_UPLOADS];

type Props = {
  IS_DRAGGING_FILE: boolean,
  SHOW_UPLOADS: boolean,
  NOTIFICATION_INFO: Object,
  [UnreadCommentsData.NAME]: UnreadCommentsData
};

type State = {
  showSettingsPopover: boolean,
  searching: boolean,
  query: string,
  showHelp: boolean,
  showNotification: boolean,
  showActivityPopover: boolean,
  loadingAssets: boolean,
  hasNextPage: boolean,
  searchFocus: boolean,
  searchedAssets: RepoItem[],
  searchedCollections: ZmCollection[],
  mobileNavVisibility: string,
  topBarVisibility: string,
  unreadCommentCount: number,
  modal: string
};

//@DragDropContext(HTML5Backend)
//@StoreListener(storeItems)
class LoggedBlock extends React.Component<Props, State> {
  static childContextTypes = {
    isNotificationPanelVisible: PropTypes.func,
    getMobileNav: PropTypes.func,
    getTopBarVisibility: PropTypes.func,
    setTopBarVisibility: PropTypes.func
  };

  static UNREAD_REFRESH_TIME = 10000;

  static MAX_AUTOSEARCH_RESULTS = 7;

  constructor(props) {
    super(props);
    this.logoSrc = this.getLogoPicUrl();
    this.state = {
      showSettingsPopover: false,
      searching: false,
      query: '',
      showHelp: false,
      showNotification: false,
      showActivityPopover: true,
      loadingAssets: true,
      hasNextPage: true,
      searchFocus: false,
      searchedAssets: [],
      searchedCollections: [],
      mobileNavVisibility: Visibility.HIDDEN,
      topBarVisibility: Visibility.VISIBLE,
      unreadCommentCount: 0,
      modal: null,
      showLoader: false,
      uploadSuccess: null,
      checkOutCD: null,
      isBucketsAvailable: false
    };

    // Need an extra variable for this because of a this.state timing issue
    // between context.getTopBarVisibility and context.setTopBarVisibility
    this._topBarVisibility = Visibility.VISIBLE;
    this._searchQueryCount = 0;

    this.onSearchQueryResponse = this.onSearchQueryResponse.bind(this);
    this.onSearchErrorOccured = this.onSearchErrorOccured.bind(this);
    this.onAutoSearchSuccess = this.onAutoSearchSuccess.bind(this);
    this.onAutoSearchError = this.onAutoSearchError.bind(this);
    this.onClearSearch = this.onClearSearch.bind(this);
  }

  getChildContext() {
    return {
      isNotificationPanelVisible: () => {
        return false; //this.state.showNotification;
      },
      getMobileNav: () => {
        return {
          visibility: this.state.mobileNavVisibility,
          close: this.closeMobileNav
        };
      },
      getTopBarVisibility: () => {
        return this._topBarVisibility;
      },
      setTopBarVisibility: (topBarVisibility) => {
        const visibleHeight = isMobile() ? 50 : 70;
        window.gs.topBarHeight = topBarVisibility === Visibility.VISIBLE ? visibleHeight : 0;
        this._topBarVisibility = topBarVisibility;
        this.setState({ topBarVisibility });
      }
    };
  }

  componentWillMount() {
    this.windowIsInFocus = true;
    this.emptyPrompt = t('PLEASE_WAIT');
    // if (System.isVAB()) {
    //   ViewUtil.setFolderNavView();
    //   Store.put(SHOW_TREE_VIEW, false);
    //   Store.put(SHOW_FOLDERNAV_VIEW, false);
    // }
    window.gs.setUploadSuccess = this.setUploadSuccess;
    if (System.isDesktop()) window.gs.setCheckOutConfirmDialogProps = this.setCheckOutConfirmDialogProps;
  }

  setCheckOutConfirmDialogProps = (checkOutProps) => {
    this.setState({ checkOutCD: checkOutProps });
  };

  notificationTest() {
    const test = {
      title: 'Notification',
      body: 'Login Success',
      onnotificationclick: function () { },
      onnotificationclose: function () { }
    };
    createNotification(test);
  }

  componentWillReceiveProps(nextProps: Props) {
    const unreadCommentsData: UnreadCommentsData = nextProps[UnreadCommentsData.NAME];
    if (!unreadCommentsData) {
      return;
    }

    if (
      unreadCommentsData !== this.props[UnreadCommentsData.NAME] ||
      unreadCommentsData.getUnreadCommentCount() !== this.state.unreadCommentCount
    ) {
      this.onUnreadCommentsDataChange(unreadCommentsData);
    }
  }

  componentWillUnmount() {
    this.removeEventListener();
    BackgroundJobRunner.JOB_RUNNER.removeJob('GetCommentsUnreadCountQuery');
    BackgroundJobRunner.JOB_RUNNER.removeJob(MaintainSessionWithPreviewServer.NAME);
    delete window.gs.setUploadSuccess;
    //window.removeEventListener('focus', this.onWindowFocus);
    //window.removeEventListener('blur', this.onWindowBlur);
    // debug('Removed onfucs and onblut window events. Cleared unread timer id.');

    window.gs.updateNotLoggedInBlock = null;
  }

  closeMobileNav = () =>
    new Promise((resolve) => {
      this.setState({ mobileNavVisibility: Visibility.HIDING });
      setTimeout(() => {
        this.setState({ mobileNavVisibility: Visibility.HIDDEN }, resolve);
      }, 100);
    });

  onUnreadCommentsDataChange = (unreadCommentData: UnreadCommentsData) => {
    debug(
      'Unread comments change event. Old Count: ',
      this.state.unreadCommentCount,
      'New Count:',
      unreadCommentData.getUnreadCommentCount()
    );
    this.setState(
      {
        unreadCommentCount: unreadCommentData.getUnreadCommentCount()
      },
      () => {
        this.state.unreadCommentCount == 0 ? this.removeShowNotification() : null;
      }
    );
  };
  removeShowNotification = () => {
    this.setState({ showNotification: false });
  };

  addEventListener = () => {
    document.addEventListener('click', this.hideNotification);
    KeyboardShortcuts.add(new KeydownConfig('LOGGED_BLOCK', this.hideNotification, [KeyCode.ESCAPE]));
  };
  removeEventListener = () => {
    document.removeEventListener('click', this.hideNotification);
    KeyboardShortcuts.remove('LOGGED_BLOCK');
  };

  fetchTargetToOps = () => {
    BrowseExtUtil.fetchBuckets()
      .then((res: ExtBrowsingCapabilities) => {
        const buckets: HashMap = res.getTargetToOps();
        if (buckets.size()) {
          this.setState({ isBucketsAvailable: true });
        }
      })
      .catch((err) => {
        ErrorHandler.handleError(err);
      });
  };

  componentDidMount() {
    this.addEventListener();
    this.hidePageLoadPlaceholder();
    this.fetchTargetToOps();
    if (System.isDesktop()) {
      const findText = window.findText;
      if (window.gs.AutoCompleteGlobalSearchRef && findText) {
        window.gs.AutoCompleteGlobalSearchRef.getSearchQuery(findText);
      }
    }
    this.notificationController = new NotificationController();
    this.initArgsProcessing();
    if (!window.gs.isExternalUser && !ViewUtil.isCollectionTabActive()) {
      new MetadataSpec().run().then(
        (resp) => window.render(),
        (error) => void 0
      );
    }

    if (!ViewUtil.isCollectionTabActive() && (window.gs.isNoRoleUser || window.gs.isExternalUser || ViewUtil.getCollectionIdFromArgs() > 0)) {
      ViewUtil.switchToCollections();
    }

    BackgroundJobRunner.JOB_RUNNER.addJob('GetCommentsUnreadCountQuery', () => {
      this.getCommentsUnreadCount();
    });
    BackgroundJobRunner.JOB_RUNNER.addJob(MaintainSessionWithPreviewServer.NAME, function () {
      new MaintainSessionWithPreviewServer();
    });
    this.getCommentsUnreadCount();
    this.getUserIdCurrentUser();
    window.gs.fullLoader = this.fullScreenLoader;
    this.logoSrc = this.getLogoPicUrl();

    window.gs.updateNotLoggedInBlock = () => {
      this.logoSrc = this.getLogoPicUrl();
      this.forceUpdate();
    };
    if (System.isVAB()) {
      setTimeout(() => {
        if (window.gs.isOpenTPM) {
          Store.put(SUCCESS_DIALOG_MSG, TPM_DIALOG_MSG);
          window.gs.isOpenTPM = false;
        }
      }, 2000);
    }
  }

  fullScreenLoader = (isShow: boolean = false) => {
    this.setState({ showLoader: isShow });
  };

  scheduleUnreadCountJob = () => {
    clearTimeout(this.unreacCountTimerId);
    this.unreacCountTimerId = setTimeout(this.getCommentsUnreadCount, LoggedBlock.UNREAD_REFRESH_TIME);
  };

  updateSetTreeExpanded = () => {
    const mode = ViewUtil.getMode();
    const isActivePWD = window.ARGS.pwd;
    const isGlobalSearchInfoActive = Store.get(GLOBAL_SEARCH_INFO)?.active;
    if (mode === 'SEARCH') {
      if (!isActivePWD) {
        SetterStoreUtil.setTreeExpanded(false);
        SetterStoreUtil.setAllFilesOption(true);
      } else {
        SetterStoreUtil.setTreeExpanded(true);
        SetterStoreUtil.setAllFilesOption(false);
      }
    } else {
      SetterStoreUtil.setTreeExpanded(true);
      SetterStoreUtil.setAllFilesOption(false);
    }
    if (isGlobalSearchInfoActive) {
      SetterStoreUtil.setTreeExpanded(false);
    }
  }

  componentDidUpdate() {
    const { isNoRoleUser, isExternalUser, linkSharingData } = window.gs;
    if (
      !ViewUtil.isCollectionTabActive() &&
      (isNoRoleUser || isExternalUser || linkSharingData || ViewUtil.getCollectionIdFromArgs() > 0) &&
      !ViewUtil.isSettingsTabActive()
    ) {
      ViewUtil.switchToCollections();
    }
    SetterStoreUtil.setShowFolderNavView(true);
  }

  setUploadSuccess = (msg: string = null) => {
    this.setState({ uploadSuccess: msg });
  };

  hidePageLoadPlaceholder = () => {
    togglePageLoadPlaceholder(true, true);
  };

  getCommentsUnreadCount = () => {
    debug('Sending query to load unread comments detail.');
    AnnotationUtil.loadAndRenderUnreadCommentDetailInWindowGlobalObject()
      .then(_.noop)
      .catch(debug);
    //Store.printStats();
  };

  getLogoPicUrl() {
    if (System.isDesktop()) {
      return window.logoExists;
    }
    return ThumbnailUtil.getLogoPicUrl();
  }

  onLoginClickForLink = () => {
    ViewUtil.reloadWindowToCollectionId(ViewUtil.getCollectionIdFromArgs());
  };

  toggleShowHelpState = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    this.setState({ showHelp: !this.state.showHelp });
  };

  toggleShowNotificationState = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!(ViewUtil.isHomeTabActive() && canShowHomeTab())) {
      this.setState({ showNotification: !this.state.showNotification });
    } else {
      if (this.refs.homeTab) this.refs.homeTab.switchActivityToCommentTab();
    }
  };

  hideNotification = (e: MouseEvent) => {
    if (this.state.showNotification) {
      if (!e.target.closest('#NotificationBell')) {
        if (
          (!e.target.closest('#ActivityPanel') || e.target.parentNode.className === 'activity-cross plain') &&
          !e.target.closest('.transparent-background')
        )
          this.setState({ showNotification: false });
      }
    }
  };

  initArgsProcessing() {
    window.gs.argsProcessed = true;
    if (!_.isEmpty(window.ARGS)) {
      const __fuid = window.ARGS['searchFuid'];
      if (!_.isEmpty(__fuid) && !ViewUtil.isCollectionTabActive()) {
        this.issueSearchOnFuid(__fuid);
        return;
      }

      const __text = window.ARGS[SearchParamUtil.TEXT];
      if (!_.isEmpty(__text)) {
        this.issueSearchOnText(__text);
        return;
      }
    }
    this.updateSetTreeExpanded();
    window.render();
  }

  executeSearchString() {
    if (this.state.searching && StringUtil.isNotEmpty(this.state.query)) {
      this.issueSearchOnText(this.state.query);
    } else {
      this.issueSearchQuery(null, null, null, false);
    }
  }

  setSearchFieldValue(val) {
    this.onSearchIconClick(false, val);
  }

  updateAssets(assets, totalCount, searchString) {
    this.setState({
      searchedAssets: assets,
      searchedCollections: Store.get(COLLECTIONS)
    });
    window.gs.totatAssets = totalCount;
    Store.put(GLOBAL_SEARCH_INFO, {
      active: true,
      assets,
      totalCount,
      hasNextPage: assets?.length < totalCount ? true : false,
      searchString
    });
    //window.render();
    if (!totalCount || totalCount <= 0) this.emptyPrompt = t('NO_RESULT_FOUND');
    this.setNextPageState(!_.isEmpty(assets));
  }

  toggleProgress(value) {
    Store.put(GLOBAL_PROGRESS, value);
  }

  checkAndcloseSearchBar() {
    if (this.state.searching) {
      this.closeSearchBar();
    }
  }

  handleClickEvent = async (activity) => {
    const __fuid = activity.getFuid();
    if (!activity.isNotClickable() && __fuid > 0) {
      if (activity.isFile()) {
        ViewUtil.switchToAssetDetailView(__fuid, true);
      } else {
        const pathIds = await getAssetPathFuids(__fuid);
        pathIds ? ViewUtil.switchToAssetsTabFolder(pathIds) : null;
      }
    }
  };

  openFuidSearch(fuid, stayOnCurrentTab) {
    debug('[openFuidSearch()] fuid: ', fuid);
    const assetIdJson = new AssetIdJson();
    assetIdJson._fuidStr = fuid;
    const assetIdJsonArray = [assetIdJson];
    this.issueSearchQuery(SearchParamUtil.ASSETID_FILTER_LIST, null, assetIdJsonArray, false, stayOnCurrentTab);
  }

  issueSearchOnFuid(fuid, stayOnCurrentTab = false) {
    debug('[issueSearchOnFuid()] args: ', { fuid, stayOnCurrentTab });
    this.openFuidSearch(fuid, stayOnCurrentTab);
  }

  updateFilters() {
    debug('[updateFilters()] Ctogry lIst ', window.gs.categoryList);
    _.each(window.gs.categoryList, (category) => {
      category.filter = '';
      category.isOpened = false;
      category.setMatchString('');

      if (category.getName2count().length > 0)
        _.each(category.getName2count(), (child) => void (child.isChecked = false));
      else {
        category.filter = '';
        _.each(category.getTreeData()[0].getChildren(), (group) => {
          _.each(group.getChildren(), (option) => void (option.isChecked = false));
        });
      }
    });
  }

  resetFilters = (searchString: string) => {
    this.issueSearchQuery(null, searchString, null, false, true, true);
  };

  async switchTab(e, tab) {
    e.preventDefault();
    // if(tab === window.ARGS.activeTab && Object.keys(window.ARGS).length == 1) {
    //   return false;
    // }
    //this.setSearchTextToEmpty();
    const globalSearchInfo = Store.get(GLOBAL_SEARCH_INFO);
    if (tab === 'HOME') {
      ViewUtil.switchToHomeTab();
      BackgroundJobRunner.JOB_RUNNER.resume();
      this.notificationController.resetTimer();
      if (globalSearchInfo && globalSearchInfo.active) {
        Store.put(GLOBAL_SEARCH_INFO, { active: false, searchString: '' });
      }
    } else {
      if (tab === 'ASSETS') {
        const categoryList = Store.get(CATEGORY_LIST);
        const fileFilters = Store.get(ASSET_FILTERS);
        if (globalSearchInfo && globalSearchInfo.active) {
          Store.put(GLOBAL_SEARCH_INFO, { active: false, searchString: '' });
        }

        if (fileFilters && fileFilters.getFilter2ValueMap().size() > 0) {
          clearAssetFilters();
          await this.issueFacetQuery(false);
        }

        const hasFilters = (categoryList || []).filter((category) => category.isHasAnySelectedItem()).length > 0;
        if (hasFilters) {
          await this.issueFacetQuery(false);
        }
        // this.updateSetTreeExpanded();
      }
      ViewUtil.switchToTab(tab, false, SUBTAB.ALL.KEY, true);
      if (ViewUtil.isAssetTabActive() && Store.get(ALL_FILES_OPTION) && !window.ARGS.type) {
        ViewUtil.setARGS({ type: ASSETS_TAB_ARGS.ALL_FILES });
      }
      this.resetErrorStateOfAssetsTab();
    }

    if (window.gs?.AutoCompleteGlobalSearchRef?.refs?.search?.value) {
      window.gs.AutoCompleteGlobalSearchRef.refs.search.value = '';
      window.gs.AutoCompleteGlobalSearchRef.setState({ query: '' });
    }
    window.render();
  }

  //Search Args Processing

  issueSearchQuery(
    dirtyField,
    text,
    assetIdJsonArray,
    isRefresh,
    stayOnCurrentTab,
    resetFilters,
    resetAllExceptFilters,
    doNotResetFilters,
    autoComplete = false
  ) {
    debug('[issueSearchQuery()] args: ', {
      dirtyField,
      text,
      assetIdJsonArray,
      isRefresh,
      stayOnCurrentTab,
      resetFilters,
      resetAllExceptFilters,
      doNotResetFilters,
      autoComplete
    });
    this.updateProgressPreSearch();
    if (!stayOnCurrentTab && !ViewUtil.isAssetTabActive()) {
      debug('[issueSearchQuery()] switching to assets tab');
      ViewUtil.switchToTab('ASSETS');
    }

    if (isRefresh) {
      debug('[issueSearchQuery()] isRefresh. Calling issueSessionReloadQuery()');
      this.issueSessionReloadQuery();
    } else {
      debug('[issueSearchQuery()] !isRefresh. Building SearchMessage');
      const criteria = new CurrentContextInfo();
      criteria.setSortCriteria(window.gs.assetTabSortCriteria);
      criteria.setOrder(window.gs.assetTabSortOrder);
      const msg = new SearchMessage();

      if (resetAllExceptFilters) {
        msg.setResetAllExceptFilters(true);
      } else {
        // msg.setAppendChangeInExistingParam(true);
        if (!StringUtil.isNull(text)) {
          msg._text = text;
        }

        if (!ArrayUtil.isNull(assetIdJsonArray)) {
          msg._assetIdFilterList = assetIdJsonArray;
        }
        if (!StringUtil.isNull(dirtyField)) msg.setDirtyArg(dirtyField);

        msg.setIsRefresh(isRefresh);
        msg.setResetFilters(resetFilters);
        msg.setCurrentContextInfo(criteria);

        if (doNotResetFilters) msg.setResetAllExceptFilters(true);
      }
      this.sendSearchQuery(msg, text);
    }
  }

  //Load Saved Filters

  issueApplyFilter(filterName) {
    this.updateProgressPreSearch();
    const query = new ApplyFilterQuery();
    const msg = new ApplyFilterMsg();
    msg.setFilterName(filterName);
    query.message = msg;
    query.run().then(
      (resp: ApplyFilterResp) => {
        this.toggleProgress(false);
        window.gs.searchhasDirtyFilters = resp.getFilesResponseData().isHasDirtyFilters();
        const searchString = resp.getSearchString();
        this.onSearchQueryResponse(resp.getFilesResponseData(), searchString);
      },
      (error) => {
        this.toggleProgress(false);
        this.onErrorInQuery(error);
      }
    );
  }

  //Facets query

  // issueFacetQuery = async ()=> {
  //   const facetQuery: GetFacetsQuery = new GetFacetsQuery();
  //   const msg = new GetFacetsMsg();
  //   msg.setUseCurrentFuidSet(true);
  //   facetQuery.message = msg;
  //   debug('[issueFacetQuery()] facelet MSG list ', facetQuery.message);
  //
  //
  //   facetQuery.run().then(
  //     (resp: GetFacetsRes) => {
  //       debug('[issueFacetQuery()] facelet query resp ', resp.getCategoryList());
  //       window.gs.categoryList = resp.getCategoryList();
  //       this.updateFilters();
  //       window.gs.progress = false;
  //       window.render();
  //     },
  //     (error) => {
  //       window.gs.progress = false;
  //       this.onErrorInQuery(error);
  //     }
  //   );
  // }

  issueFacetQuery = async (useCurrentFuidSet: boolean = true) => {
    try {
      const query = new GetFacetsQuery();
      const msg: GetFacetsMsg = new GetFacetsMsg();
      msg.setUseCurrentFuidSet(useCurrentFuidSet);
      query.message = msg;
      debug('[issueFacetQuery()] running query: ', query);
      const res: GetFacetsRes = await query.run();
      debug('[issueFacetQuery()] response: ', res);
      const categoryList = res.getCategoryList();
      Store.put(CATEGORY_LIST, categoryList);
    } catch (err) {
      debug('[issueFacetQuery()] error: ', err);
    }
  };

  //Search Related Queries

  issueSessionReloadQuery() {
    this.updateProgressPreSearch();
    const query = new ReloadSessionQuery();
    const msg = new ReloadSessionMsg();
    query.message = msg;
    query.run().then(
      (resp) => {
        window.gs.searchhasDirtyFilters = resp.isHasDirtyFilters();
        this.resetProgressPostSearch();
        this.emptyPrompt = '';
        this.updateAssets(resp.getResultFiles(), resp.getTotalFiles());
        this.setState({ loadingAssets: false });
        window.gs.categoryList = resp.getCategoryList();
        window.render();
      },
      (error) => {
        this.onSearchErrorOccured(error);
      }
    );
  }

  onCategoryChangeEvent(categoryId, selected, facetedItemInfo, parentNode) {
    this.updateProgressPreSearch();
    const query = new CategoryChangeQuery();
    const msg = new CategoryChangeMsg();
    msg.setCategoryId(categoryId);
    let type2ItemMap = new HashMap();
    type2ItemMap.put('actualnode', facetedItemInfo);
    if (parentNode) type2ItemMap.put('parentnode', parentNode);
    msg.setTypeToFacetedItemMap(type2ItemMap);
    msg.setSelected(selected);
    query.message = msg;
    query.run().then(
      (resp) => {
        window.gs.searchhasDirtyFilters = resp.isHasDirtyFilters();
        this.updateAssets(resp.getResultFiles(), resp.getTotalFiles());

        const selectedAssets = window.gs.selectedAssets || [];
        if (selectedAssets.length) {
          debug('[onCategoryChangeEvent()] updating gs.selectedAssets');
          const fuids = window.gs.assets.map((asset: RepoItem) => asset.getFuid());
          window.gs.selectedAssets = selectedAssets.filter((asset: RepoItem) => _.includes(fuids, asset.getFuid()));
        }

        window.gs.categoryList = resp.getCategoryList();
        this.resetProgressPostSearch();
        window.render();
      },
      (error) => {
        this.resetProgressPostSearch();
        this.onErrorInQuery(error);
      }
    );
  }

  setNextPageState(value) {
    this.setState({ hasNextPage: value });
  }

  loadNextPage() {
    window.gs.blockSearch = true;
    this.setState({ loadingAssets: true });
    const query = new LoadNextSearchPageQuery();
    query.message = new LoadNextSearchPageMsg();
    query.run().then(
      (resp) => {
        window.gs.blockSearch = false;
        window.gs.searchhasDirtyFilters = resp.isHasDirtyFilters();
        if (_.isEmpty(resp.getResultFiles())) this.setNextPageState(false);
        else {
          this.setNextPageState(true);
          if (resp.isCollectiveResponseUpToCurrentPage()) window.gs.assets = resp.getResultFiles();
          else window.gs.assets = window.gs.assets.concat(resp.getResultFiles());
          window.gs.totatAssets = resp.getTotalFiles();
        }
        //MetadataUtil.addAssetIdInMetadataMapOfAsset();
        this.setState({ loadingAssets: false });
      },
      (error) => {
        window.gs.blockSearch = false;
        ErrorHandler.handleError(error);
        this.setState({ loadingAssets: false });
      }
    );
  }

  sendSearchQuery(msg: SearchMessage, searchString: ?string) {
    debug('[sendSearchQuery()] msg: ', msg);
    const searchQuery = new SearchQuery();
    searchQuery.message = msg;
    searchQuery.run().then(
      (resp: FilesResponseData) => {
        debug('[sendSearchQuery()] resp: ', resp);
        //this.setState({ loadingAssets: false });
        this.resetProgressPostSearch();
        window.gs.searchhasDirtyFilters = resp.isHasDirtyFilters();
        if (!resp.isNoChange()) this.onSearchQueryResponse(resp.getResultFiles(), searchString);
        else window.render();
      },
      (error) => {
        debug('[sendSearchQuery()] error');
        this.onSearchErrorOccured(error);
        if (ViewUtil.getFuid()) {
          debug('[sendSearchQuery()] removing fuid from url');
          ViewUtil.removeArg('fuid');
        }
      }
    );
  }

  onAutoSearchSuccess(resp) {
    const searchedAssets = resp.getMatchedAssets();
    const searchedCollections = resp.getMatchedCollection();
    this.setState({
      searchedAssets,
      searchedCollections
    });
    // timeout is added to avoid quick re-rendering of progress bar since search can be fast some times
    setTimeout(() => {
      this.resetProgressPostSearch();
      window.render();
    }, 1000);
  }

  onAutoSearchError() {
    // error argument can be used to display error message from backend in future
    this.setState({
      searchedAssets: [],
      searchedCollections: []
    });
  }

  onClearSearch() {
    // If on assets tab issue a new search query
    if (ViewUtil.isAssetTabActive()) {
      this.issueSearchOnText('');
    }
    // this added when there is an error message on auto and you press close icon, it still shows error message, reset everything to initial
    this.setState({
      searchedAssets: [],
      searchedCollections: [],
      query: ''
    });
  }

  issueSearchOnText(val) {
    window.gs.selectedAssets = [];
    this.issueSearchQuery(SearchParamUtil.TEXT, val, null, false, false, false, false, true);
  }

  //Response and Error Handling

  onSearchErrorOccured(error) {
    this.resetProgressPostSearch();
    this.emptyPrompt = t('NO_RESULT_FOUND');
    window.gs.assets = null;
    window.gs.categoryList = null;
    window.gs.searchhasDirtyFilters = false;
    this.onErrorInQuery(error, true);
  }

  resetProgressPostSearch() {
    window.gs.blockSearch = false;
    this.toggleProgress(false);
    this.setState({ loadingAssets: false });
  }

  updateProgressPreSearch() {
    window.gs.blockSearch = true;
    this.toggleProgress(true);
  }

  onSearchQueryResponse(resp: FilesResponseData, searchString: string) {
    debug('[onSearchQueryResponse()] response: ', resp);
    this.updateAssets(resp.getResultFiles(), resp.getTotalFiles(), searchString);
    const contextInfo = resp.getCurrentContextInfo();
    if (contextInfo) {
      window.gs.assetTabSortCriteria = contextInfo.getSortCriteria();
    }
    this.issueFacetQuery();

    if (this._searchQueryCount === 0) {
      this.onInitialSearchQueryComplete();
    }
    this._searchQueryCount++;
  }

  onInitialSearchQueryComplete = () => {
    const fuid = ViewUtil.getFuid();
    if (fuid && !window.gs.assets.find((a: RepoItem) => a.getFuid() === fuid)) {
      debug(
        `[onInitialSearchQueryComplete()] couldn't find fuid ${fuid} in initial search results. Running fuid search.`
      );
      this.issueSearchOnFuid(fuid);
    }
  };

  onErrorInQuery(error, onlyLog) {
    ErrorHandler.handleError(error, null, onlyLog);
  }

  handleAccountClick = () => {
    this.setState({
      //For link sharing data, we don't display and user account popup...
      modal: !window.gs.linkSharingData ? 'account' : null
    });
  };

  hideEditUserAccountModal = () => {
    this.setState({
      modal: null
    });
  };

  resetErrorStateOfAssetsTab = () => {
    if (this._assetsTab) {
      this._assetsTab.resetErrorState();
    }
  };

  onClearSelectedItems = () => {
    if (this._assetsTab) {
      this._assetsTab.onClearSelectedItems();
    } else if (this._collectionsTab) {
      this._collectionsTab._component.onClearSelectedItems();
    }
  };

  getUserIdCurrentUser = async () => {
    const currentUserEmail = await ThumbnailUtil.fetchEmailAddress(Authenticator.AUTH_DATA.username);
    window.gs.currentUserEmail = currentUserEmail;
  };

  selectAll = (e: KeyboardEvent, onSelectAllItems) => {
    if ((e.ctrlKey || e.metaKey) && e.keyCode === KeyCode.A) {
      e.preventDefault();
      onSelectAllItems();
    }
  };

  render() {
    const {
      searchedAssets,
      searchedCollections,
      topBarVisibility,
      showHelp,
      showNotification,
      showLoader,
      uploadSuccess,
      checkOutCD,
      isBucketsAvailable
    } = this.state;
    const { detail, fuid } = getParamsFromUrl();
    const isAssetDetailView: boolean = detail && fuid;
    const noTopBar = ViewUtil.isHomeTabActive() && isMobile();
    const globalSearch = canShowSearchTab() ? (
      <AutoCompleteGlobalSearch
        ref="autoCompleteGlobalSearch"
        searchedAssets={searchedAssets}
        searchedCollections={searchedCollections}
        onClearSearch={this.onClearSearch}
        onSearchSuccess={this.onSearchQueryResponse}
        onSearchError={this.onSearchErrorOccured}
        onAutoSearchSuccess={this.onAutoSearchSuccess}
        onAutoSearchError={this.onAutoSearchError}
        maxSearchResult={LoggedBlock.MAX_AUTOSEARCH_RESULTS}
        issueFacetQuery={() => this.issueFacetQuery(false)}
        onClearSelectedItems={this.onClearSelectedItems}
      />
    ) : null;
    const isDragging = this.props.IS_DRAGGING_FILE;
    const showUploads = this.props.SHOW_UPLOADS;
    const styles = getStyle();
    return (
      <div
        name="LoggedBlock"
        onClick={this.hideNotification}
        className={classNames(getScreenClassName(), {
          'link-user': ViewUtil.isDirectLinkToCollection() && (window.gs.isExternalUser || window.gs.isNoRoleUser)
        })}>
        <div className="wrapper">
          <Toast />
          <SuccessToast />
          {checkOutCD ? (
            <ConfirmModal
              onClose={checkOutCD.onClose}
              message={checkOutCD.msg}
              title={checkOutCD.title}
              moveAsset={checkOutCD.onYes}
              onCross={checkOutCD.onCross}
            />
          ) : null}
          {uploadSuccess ? (
            <SuccessDailog msg={uploadSuccess} closeModal={() => this.setState({ uploadSuccess: null })} />
          ) : null}
          {showLoader ? <LoaderSpinner /> : null}
          {showUploads ? <UploadingsModal /> : null}
          {isDragging > 0 && !CollectionUtil.isLink() ? <DropzoneModal /> : null}
          {isMobile() ? (
            <MobileHeader
              globalSearch={globalSearch}
              className={topBarVisibility.toLowerCase()}
              onAccountClick={this.handleAccountClick}
              onClickEvent={this.handleClickEvent}
              unreadCommentCount={this.state.unreadCommentCount}
            />
          ) : (
            <div id="LoggedBlockTopToolBar" className={topBarVisibility.toLowerCase()}>
              {System.isDesktop() ? <GoBackAndForthPages assetTab={this._assetsTab} /> : null}
              <div id="navigationLinks" className={`flex-row ${System.isDesktop() ? '' : 'padding-left-50'}`}>
                {
                  canShowHomeTab() ? (
                    <A
                      className="home"
                      href={ParamsUtil.appendStateParams({ activeTab: TABS.HOME.KEY })}
                      onClick={(e) => this.switchTab(e, TABS.HOME.KEY)}
                      title={window.ARGS.activeTab ? t('GO_BACK_HOME') : ''}>
                      {window.logoExists ? <img src={this.logoSrc} /> : <img src="images/logo.png" />}
                    </A>
                  ) : ViewUtil.isDirectLinkToCollection() ? (
                    <div className="flex-main flex-row align-center spc-between">
                      <div className="home">
                        {window.logoExists ? <img src={this.logoSrc} /> : <img src="images/logo.png" />}
                      </div>
                      <PublicBreadcrumb />
                    </div>
                  ) : (
                    <A className="home disabled" title={window.gs.companyName}>
                      {window.logoExists ? <img src={this.logoSrc} /> : <img src="images/logo.png" />}
                    </A>
                  )
                }
                {
                  canShowAssetsTab() ? (
                    <A
                      href={ParamsUtil.appendStateParams({ activeTab: TABS.ASSETS.KEY })}
                      className={classNames({
                        active: ViewUtil.isAssetTabActive() || isAssetDetailView
                      })}
                      onClick={(e) => this.switchTab(e, TABS.ASSETS.KEY)}
                      style={{ width: '100px' }}>
                      <span>{TABS.ASSETS.LABEL}</span>
                    </A>
                  ) : null
                }
                {
                  canShowCollectionsTab() ? (
                    <A
                      href={ParamsUtil.appendStateParams({ activeTab: TABS.COLLECTIONS.KEY })}
                      className={classNames({
                        active: ViewUtil.isCollectionTabActive() && !isAssetDetailView
                      })}
                      onClick={(e) => this.switchTab(e, TABS.COLLECTIONS.KEY)}
                      style={{ width: '150px' }}>
                      <span>{TABS.COLLECTIONS.LABEL}</span>
                    </A>
                  ) : window.gs.isNoRoleUser && !ViewUtil.isDirectLinkToCollection() ? (
                    <A className={classNames('disabled', { active: ViewUtil.isCollectionTabActive() })}>
                      <span>{TABS.COLLECTIONS.LABEL}</span>
                    </A>
                  ) : null
                }
                {
                  canShowBrowseExtTab() && isBucketsAvailable ? (
                    <A
                      href={ParamsUtil.appendStateParams({ activeTab: TABS.EXTERNAL_FILES.KEY })}
                      className={classNames({
                        active: ViewUtil.isBrowseExternalTabActive() && !isAssetDetailView
                      })}
                      onClick={(e) => this.switchTab(e, TABS.EXTERNAL_FILES.KEY)}
                      style={{ width: '150px' }}>
                      <span style={{ whiteSpace: 'nowrap' }}>{TABS.EXTERNAL_FILES.LABEL}</span>
                    </A>
                  ) : null
                }
                {
                  showReportingTab() ? (
                    <A
                      href={ParamsUtil.appendStateParams({ activeTab: TABS.REPORTING.KEY })}
                      className={classNames({ active: ViewUtil.isReportingTabActive() })}
                      onClick={(e) => this.switchTab(e, TABS.REPORTING.KEY)}>
                      <span>{TABS.REPORTING.LABEL}</span>
                    </A>
                  ) : null
                }
                {globalSearch}
              </div>
              {
                !window.gs.isExternalUser && !window.gs.isNoRoleUser ? (
                  <HelpIcon isSelected={showHelp} onClick={this.toggleShowHelpState} />
                ) : null
              }
              <div id="notifications" className="flex-row align-center">
                <UploadingsItem />
                {/* {System.isDesktop() ? <GoBackAndForthPages assetTab={this._assetsTab} /> : null} */}
                <NotificationIcon
                  unreadCommentCount={this.state.unreadCommentCount}
                  styles={{
                    notificationCount: styles.notificationCount,
                    notificationIcon: styles.notificationIcon
                  }}
                  toggleShowNotificationState={this.toggleShowNotificationState}
                />
                <Action custom predicates={[() => !window.gs.isLinkUser]}>
                  <UserPopover userId={Authenticator.AUTH_DATA.username} />
                </Action>
              </div>
              <Button
                icon="images/h-menu.svg"
                className={this.state.mobileNavVisibility.toLowerCase()}
                onClick={() => this.setState({ mobileNavVisibility: Visibility.VISIBLE })}
              />
              {
                this.state.showSettingsPopover ? (
                  <SettingsPopover
                    right={this.refs.settings.getBoundingClientRect().right}
                    top={this.refs.settings.getBoundingClientRect().bottom}
                    onClose={() => this.setState({ showSettingsPopover: false })}
                  />
                ) : null
              }
            </div>
          )}
          <div className="main-content flex-row" style={{ paddingTop: noTopBar || isMobile() ? 0 : 70 }}>
            {
              showHelp ? (
                <Popover
                  closeOnEsc={true}
                  defaultOpen={showHelp}
                  onClose={this.toggleShowHelpState}
                  top={'75px'}
                  right={'3%'}>
                  <HelpWrapper
                    helpKey={HelpUtil.getHelpKeyForPage(ViewUtil.getActiveTab())}
                    onClose={this.toggleShowHelpState}
                  />
                </Popover>
              ) : null
            }
            {
              showNotification ? (
                <NotificationPanel
                  hideNotification={this.hideNotification}
                  activities={window.gs.notificationInfo.activities}
                  onClose={() => this.setState({ showNotification: false })}
                  onClickEvent={(activity) => this.handleClickEvent(activity)}
                />
              ) : null
            }
            {
              ViewUtil.isHomeTabActive() && canShowHomeTab() ? (
                <HomeTab
                  ref="homeTab"
                  issueSearchQuery={this.issueSearchQuery.bind(this)}
                  onClickEvent={(activity) => this.handleClickEvent(activity)}
                  issueSearchOnFuid={(fuid) => this.issueSearchOnFuid(fuid, true)}
                />
              ) : null
            }
            {
              ViewUtil.isBrowseExternalTabActive() ? <BrowseExt></BrowseExt> : null
            }
            {
              ViewUtil.isAssetTabActive() && canShowAssetsTab() ? (
                <AssetsTab
                  ref={(node) => (this._assetsTab = node)}
                  selectAll={this.selectAll}
                  hideNotification={this.hideNotification}
                  emptyPrompt={this.emptyPrompt}
                  showNotification={showNotification}
                  loadingAssets={this.state.loadingAssets}
                  hasNextPage={this.state.hasNextPage}
                  loadNextPage={this.loadNextPage.bind(this)}
                  issueSearchQuery={(
                    dirtyField,
                    text,
                    assetIdJsonArray,
                    isRefresh,
                    resetAllExceptFilters,
                    stayOnCurrentTab,
                    resetFilters
                  ) =>
                    this.issueSearchQuery(
                      dirtyField,
                      text,
                      assetIdJsonArray,
                      isRefresh,
                      stayOnCurrentTab,
                      resetFilters,
                      resetAllExceptFilters
                    )
                  }
                  executeSearchString={() => this.executeSearchString()}
                  resetFilters={(searchString) => this.resetFilters(searchString)}
                  issueFacetQuery={this.issueFacetQuery}
                  issueApplyFilter={(filterName) => this.issueApplyFilter(filterName)}
                  onCategoryChangeEvent={(categoryId, selected, facetedItemInfo, parentNode) =>
                    this.onCategoryChangeEvent(categoryId, selected, facetedItemInfo, parentNode)
                  }
                />
              ) : null
            }
            {
              ViewUtil.isCollectionTabActive() ? (
                <CollectionsTab
                  ref={(node) => (this._collectionsTab = node)}
                  selectAll={this.selectAll}
                  showNotification={false}
                  hideNotification={this.hideNotification}
                />
              ) : null
            }
            {
              ViewUtil.isReportingTabActive() ? (
                <ReportingTab fetchAssets={() => this.issueSearchQuery(null, null, null, false, true)} />
              ) : null
            }
            {
              ViewUtil.isAdvancedSearchTabActive() ? (
                <AdvancedSearch fetchAssets={() => this.issueSearchQuery(null, null, null, false, true)} />
              ) : null
            }
            {
              ViewUtil.isSettingsTabActive() ? <SettingsTab /> : null
            }
          </div>
        </div>
        <ShowEditUserAccount show={this.state.modal === 'account'} onClose={this.hideEditUserAccountModal} />
      </div>
    );
  }
}

type ShowEditUserAccountProps = {
  show: boolean,
  onClose: () => void
};
const ShowEditUserAccount = function (props: ShowEditUserAccountProps) {
  if (props.show === true) {
    return <EditUserAccount onClose={props.onClose} />;
  }
  return null;
};

function getStyle() {
  return {
    topToolbar: {
      height: '70px',
      background: 'white',
      display: 'flex',
      justifyContent: 'space-between',
      verticalAlign: 'middle'
    },
    searchItem: {
      cursor: 'pointer',
      fontSize: '12px',
      fontWeight: '600',
      margin: '26px 26px 0 26px',
      paddingBottom: '26px',
      backgroundImage: ThemeSetup.getBackgroundURL('search.svg'),
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '0 0',
      paddingLeft: '20px',
      color: '#EB6A5C',
      lineHeight: '18px',
      letterSpacing: '1px'
    },
    userPopover: {
      padding: '15px 15px 15px 15px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      color: '#7A7A7A',
      fontSize: '12px',
      fontWeight: 'bold',
      lineHeight: '18px',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '100% 50%',
      marginRight: '20px'
    },
    notificationIcon: {
      position: 'relative',
      width: '20px',
      height: '20px',
      margin: '0 30px 0 25px',
      cursor: 'pointer',
      backgroundImage: ThemeSetup.getBackgroundURL('icon_notification.svg'),
      backgroundSize: '100%',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    },
    notificationCount: {
      position: 'absolute',
      top: '-7px',
      left: '15px',
      borderRadius: '35%',
      fontWeight: '600',
      color: 'var(--primary-header-bg)',
      fontSize: '10px',
      lineHeight: '15px',
      letterSpacing: '1px',
      padding: '3px 4px 2px 5px',
      background: 'var(--primary-style-color)'
    },
    settingsPopover: {
      float: 'left',
      padding: '26px 15px 26px 26px',
      textTransform: 'upper-case',
      cursor: 'pointer',
      color: '#7A7A7A',
      fontSize: '12px',
      fontWeight: 'bold',
      lineHeight: '18px',
      backgroundImage: 'url(images/arrow_down.svg)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: '100% 50%'
    }
  };
}

const WrappedLoggedBlock = StoreListener(storeItems, Store.GLOBAL_STORE)(LoggedBlock);
function DragDropContext() {
  return (
    <DndProvider backend={HTML5Backend}>
      <WrappedLoggedBlock />
    </DndProvider>
  );
}

export default GlobalShortcuts(DragDropContext);
